import { type FC, useState } from 'react';

import type { ActionItemVm } from '@eluve/api-contract';
import { allRowsConfirmed, itemReviewPosture } from '@eluve/api-contract';
import { Button, Icon, Input, Stack, Text } from '@eluve/components';
import { useTranslation } from '@eluve/i18n';

import type { CarePlanEvidenceSelectionHandler } from './CarePlanEvidenceDrawer';
import { ItemEditForm } from './ItemEditForm';
import { ItemReviewRow } from './ItemReviewRow';
import { PendingChangeDiff } from './PendingChangeDiff';
import { SectionBulkControls } from './SectionBulkControls';
import { changeByField, confirmableRows } from './proposal-rows';

const ActionItemEditForm: FC<{
  initial: string;
  disabled?: boolean;
  onCancel: () => void;
  onSave: (text: string) => void;
  onDelete?: () => void;
}> = ({ initial, disabled, onCancel, onSave, onDelete }) => {
  const { t } = useTranslation();
  const [text, setText] = useState(initial);
  const isEdit = Boolean(onDelete);

  return (
    <ItemEditForm
      title={
        isEdit
          ? t('carePlans.actionItems.editTitle')
          : t('carePlans.actionItems.addActionItem')
      }
      saveLabel={
        isEdit ? t('carePlans.review.saveChanges') : t('carePlans.review.save')
      }
      saveDisabled={text.trim().length === 0}
      disabled={disabled}
      onCancel={onCancel}
      onSave={() => onSave(text.trim())}
      onDelete={onDelete}
      deleteLabel={t('carePlans.actionItems.deleteActionItem')}
      deleteConfirmTitle={t('carePlans.actionItems.deleteConfirmTitle')}
      deleteConfirmBody={t('carePlans.actionItems.deleteConfirmBody')}
    >
      <Input
        value={text}
        disabled={disabled}
        onChange={(e) => setText(e.target.value)}
        placeholder={t('carePlans.actionItems.textPlaceholder')}
        aria-label={t('carePlans.actionItems.textAriaLabel')}
        className="mt-3"
      />
    </ItemEditForm>
  );
};

export const ActionItemsSection: FC<{
  actionItems: ActionItemVm[];
  disabled?: boolean;
  showBulkUndo?: boolean;
  onConfirm: (item: ActionItemVm) => void;
  onConfirmAll?: (items: ActionItemVm[]) => void;
  onUndoConfirmAll?: () => void;
  onReject: (item: ActionItemVm) => void;
  onUndo: (item: ActionItemVm) => void;
  onEditText: (item: ActionItemVm, text: string) => void;
  onDelete: (item: ActionItemVm) => void;
  onAdd: (text: string) => void;
  onSelectEvidence?: CarePlanEvidenceSelectionHandler;
}> = ({
  actionItems,
  disabled,
  showBulkUndo,
  onConfirm,
  onConfirmAll,
  onUndoConfirmAll,
  onReject,
  onUndo,
  onEditText,
  onDelete,
  onAdd,
  onSelectEvidence,
}) => {
  const { t } = useTranslation();
  const [editingId, setEditingId] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);

  const confirmable = confirmableRows(actionItems);
  const allConfirmed = allRowsConfirmed(actionItems.map(itemReviewPosture));

  return (
    <section className="w-full rounded-xl border border-border-secondary bg-bg-primary p-4 shadow-sm">
      <SectionBulkControls
        title={t('carePlans.actionItems.title')}
        confirmableCount={confirmable.length}
        allConfirmed={allConfirmed}
        showUndoAll={Boolean(showBulkUndo)}
        disabled={disabled}
        onConfirmAll={() =>
          onConfirmAll
            ? onConfirmAll(confirmable)
            : confirmable.forEach(onConfirm)
        }
        onUndoAll={onUndoConfirmAll}
      />

      {actionItems.length === 0 && !adding ? (
        <Text size="sm" color="secondary" className="mt-3 block">
          {t('carePlans.actionItems.empty')}
        </Text>
      ) : (
        <Stack direction="vertical" gap={2} className="mt-3">
          {actionItems.map((item) => {
            const textChange = changeByField(item, 'text');
            const evidenceLabel = textChange?.proposed ?? item.text;
            const sourceEvidence = item.sourceEvidence;
            return editingId === item.id ? (
              <ActionItemEditForm
                key={item.id}
                initial={item.text}
                disabled={disabled}
                onCancel={() => setEditingId(null)}
                onSave={(text) => {
                  onEditText(item, text);
                  setEditingId(null);
                }}
                onDelete={() => {
                  onDelete(item);
                  setEditingId(null);
                }}
              />
            ) : (
              <ItemReviewRow
                key={item.id}
                itemState={item.itemState}
                decision={item.decision}
                title={
                  textChange && sourceEvidence && onSelectEvidence ? (
                    <PendingChangeDiff change={textChange} />
                  ) : textChange ? undefined : (
                    item.text
                  )
                }
                disabled={disabled}
                sourceLabel={
                  sourceEvidence && onSelectEvidence ? evidenceLabel : undefined
                }
                onSelectSource={
                  sourceEvidence && onSelectEvidence
                    ? (triggerElement) =>
                        onSelectEvidence(
                          {
                            label: evidenceLabel,
                            itemType: 'action-item',
                            evidence: sourceEvidence,
                          },
                          triggerElement,
                        )
                    : undefined
                }
                onConfirm={() => onConfirm(item)}
                onReject={() => onReject(item)}
                onUndo={() => onUndo(item)}
                onEdit={() => setEditingId(item.id)}
              >
                {textChange && (!sourceEvidence || !onSelectEvidence) ? (
                  <PendingChangeDiff change={textChange} />
                ) : null}
              </ItemReviewRow>
            );
          })}
          {adding && (
            <ActionItemEditForm
              initial=""
              disabled={disabled}
              onCancel={() => setAdding(false)}
              onSave={(text) => {
                onAdd(text);
                setAdding(false);
              }}
            />
          )}
        </Stack>
      )}
      {!adding && (
        <Button
          size="sm"
          variant="outline"
          disabled={disabled}
          onClick={() => setAdding(true)}
          className="mt-3"
        >
          <Icon name="Plus" size="auto" />
          {t('carePlans.actionItems.addActionItem')}
        </Button>
      )}
    </section>
  );
};
